// QuickCategoryModal.js — быстрое добавление категории (income/expense)
// Используется в OpsTab для добавления категории прямо из формы операции.
import React, { useState, useRef, useEffect } from 'react';
import { X, Check, Plus, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useTheme, uid, Btn, addAuditLog, HelpTooltip } from './shared';
import { HELP_TEXTS } from './HelpText';
import { S } from './strings';

export function QuickCategoryModal({ data, uOrg, user, isIncome, onClose, onCreated, apiSync }) {
  const t = useTheme();
  const [form, setForm] = useState({
    name: '',
    isIncome: !!isIncome,
    accountId: '',
    fundId: '',
    contractorIds: [],
  });
  const [cSearch, setCSearch] = useState('');
  const [showCDropdown, setShowCDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const save = () => {
    if (!form.name.trim()) return;
    let nc;
    if (form.isIncome) {
      nc = {
        id: `inc_${uid()}`,
        archived: false,
        name: form.name.trim(),
        accountId: form.accountId || '',
        contractorIds: form.contractorIds || [],
      };
      uOrg(p => ({ ...p, incomeCategories: [...(p.incomeCategories || []), nc] }));
    } else {
      nc = {
        id: `cat_${uid()}`,
        archived: false,
        name: form.name.trim(),
        fundId: form.fundId || '',
        contractorIds: form.contractorIds || [],
      };
      uOrg(p => ({ ...p, expenseCategories: [...(p.expenseCategories || []), nc] }));
    }
    addAuditLog(uOrg, user, {
      type: 'CAT_CREATE',
      desc: `Создана категория: ${nc.name}`,
      after: { name: nc.name, isIncome: form.isIncome },
    });
    // Stage 20b B.3: REST POST /categories с type=income/expense.
    if (apiSync && apiSync.syncCreateCategory) {
      apiSync.syncCreateCategory({ ...nc, type: form.isIncome ? 'income' : 'expense' });
    }
    if (onCreated) onCreated(nc.id, form.isIncome);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 16, padding: 24, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.4)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{S().data.newCategory}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <HelpTooltip text={HELP_TEXTS.category} size={15} />
            <button onClick={onClose} style={{ background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="radio" checked={form.isIncome} onChange={() => setForm({ ...form, isIncome: true, fundId: '' })} name="qctype" />
            <span style={{ fontSize: 13, fontWeight: 700, color: t.accentGreen }}>{S().data.incomeType}</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="radio" checked={!form.isIncome} onChange={() => setForm({ ...form, isIncome: false, accountId: '' })} name="qctype" />
            <span style={{ fontSize: 13, fontWeight: 700, color: t.accentRed }}>{S().data.expenseType}</span>
          </label>
        </div>

        <input
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          placeholder={S().data.categoryNamePlaceholder}
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, marginBottom: 16 }}
          autoFocus
        />

        {form.isIncome ? (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().data.linkToAccountLabel}</label>
            <select value={form.accountId || ''} onChange={e => setForm({ ...form, accountId: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text }}>
              <option value="">{S().data.noAccountLink}</option>
              {(data.accounts || []).filter(a => !a.archived).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: t.textMuted, fontWeight: 700, marginBottom: 6, display: 'block' }}>{S().data.linkToFundLabel}</label>
            <select value={form.fundId || ''} onChange={e => setForm({ ...form, fundId: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text }}>
              <option value="">{S().data.noFundLink}</option>
              {(data.funds || []).filter(f => !f.archived).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
        )}

        <div style={{ marginBottom: 20, padding: 12, background: t.bgInput, borderRadius: 10, border: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{S().data.linkedContractors}</div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {(form.contractorIds || []).map(cId => {
              const con = (data.contractors || []).find(x => x.id === cId);
              if (!con) return null;
              return (
                <div key={cId} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', fontSize: 12, borderRadius: 14, background: t.accent, color: '#fff', fontWeight: 600 }}>
                  {con.name}
                  <X size={14} style={{ cursor: 'pointer', opacity: 0.8 }} onClick={() => setForm(p => ({ ...p, contractorIds: p.contractorIds.filter(x => x !== cId) }))} />
                </div>
              );
            })}
            {(form.contractorIds || []).length === 0 && <span style={{ fontSize: 12, color: t.textMuted }}>{S().data.noneSelected}</span>}
          </div>

          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: t.bgCard, borderRadius: 8, border: `1px solid ${t.border}`, padding: '0 10px' }}>
              <Search size={14} color={t.textMuted} />
              <input
                value={cSearch}
                onChange={e => { setCSearch(e.target.value); setShowCDropdown(true); }}
                onFocus={() => setShowCDropdown(true)}
                placeholder={S().data.searchByNameOrBin}
                style={{ flex: 1, padding: '10px', border: 'none', background: 'transparent', fontSize: 13, color: t.text, outline: 'none' }}
              />
              {showCDropdown ? (
                <ChevronUp size={16} color={t.textMuted} style={{ cursor: 'pointer' }} onClick={() => setShowCDropdown(false)} />
              ) : (
                <ChevronDown size={16} color={t.textMuted} style={{ cursor: 'pointer' }} onClick={() => setShowCDropdown(true)} />
              )}
            </div>

            {showCDropdown && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8, maxHeight: 200, overflowY: 'auto', zIndex: 50, boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                {(() => {
                  const available = (data.contractors || []).filter(c => !c.archived && (form.isIncome ? c.isCustomer !== false : c.isSupplier !== false));
                  const filtered = available.filter(c => (c.name || '').toLowerCase().includes(cSearch.toLowerCase()) || (c.bin || '').includes(cSearch));
                  if (filtered.length === 0) return <div style={{ padding: 12, fontSize: 13, color: t.textMuted, textAlign: 'center' }}>{S().data.contractorsNotFound}</div>;
                  return filtered.map(c => {
                    const isSelected = form.contractorIds?.includes(c.id);
                    return (
                      <div
                        key={c.id}
                        onClick={() => setForm(p => ({ ...p, contractorIds: isSelected ? p.contractorIds.filter(x => x !== c.id) : [...(p.contractorIds || []), c.id] }))}
                        style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${t.border}`, background: isSelected ? `${t.accent}15` : 'transparent', transition: '0.2s' }}
                      >
                        <div>
                          <div style={{ fontWeight: isSelected ? 800 : 600, color: t.text }}>{c.name}</div>
                          {c.bin && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{S().data.binIinLabel}{c.bin}</div>}
                        </div>
                        {isSelected ? <Check size={16} color={t.accent} /> : <Plus size={16} color={t.textMuted} style={{ opacity: 0.5 }} />}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>

        <Btn variant="success" style={{ width: '100%', justifyContent: 'center', padding: 14 }} onClick={save}>
          <Check size={18} /> {S().common.save}
        </Btn>
      </div>
    </div>
  );
}

export default QuickCategoryModal;
